import type { Locale } from "@/lang";

// Pages where each language has its own URL: FR at the base path, EN at
// `.../en`. On these pages the URL — not the saved preference — decides the
// rendered language; everywhere else (e.g. /team) language is a client-side
// swap on a single URL. Client-safe on purpose (no node imports): imported
// by IntlProvider and nav components.
const STATIC_DUAL_BASES = new Set([
  "/",
  "/legal-notice",
  "/terms-of-sale",
  "/privacy",
  "/terms",
  "/refund",
  "/card",
  "/guide",
]);

// /resources, /resources/<category>, /resources/<category>/<slug>
const RESOURCES_BASE_RE = /^\/resources(?:\/[^/]+){0,2}$/;

function splitEnSuffix(pathname: string): { base: string; isEn: boolean } {
  const trimmed = pathname.replace(/\/+$/, "") || "/";
  const isEn = trimmed === "/en" || trimmed.endsWith("/en");
  const base = isEn ? trimmed.replace(/\/en$/, "") || "/" : trimmed;
  return { base, isEn };
}

function isDualBase(base: string): boolean {
  return STATIC_DUAL_BASES.has(base) || RESOURCES_BASE_RE.test(base);
}

/** Locale implied by the URL, or null when the page has no per-language URL. */
export function localeFromPathname(pathname: string): Locale | null {
  const { base, isEn } = splitEnSuffix(pathname);
  if (!isDualBase(base)) return null;
  return isEn ? "en" : "fr";
}

/** Twin URL of `pathname` in `target` locale, or null when not a dual-URL page. */
export function localePath(pathname: string, target: Locale): string | null {
  const { base, isEn } = splitEnSuffix(pathname);
  if (!isDualBase(base)) return null;
  if (target === "en") return isEn ? pathname : base === "/" ? "/en" : `${base}/en`;
  return base;
}

/** Homepage URL for a locale — for logo/home links, so they don't drop the language. */
export function homePath(locale: Locale): string {
  return locale === "en" ? "/en" : "/";
}

/** Is this pathname the homepage (either language)? */
export function isHomePath(pathname: string): boolean {
  const { base } = splitEnSuffix(pathname);
  return base === "/";
}
